import type { StaffRefundDoor } from '../../lib/api/staffRefund';
import type { View } from './types';
import { clubEffectiveCourts, clubPhysicalCourts } from '../../lib/clubCourts';
import { BookingView } from '../../components/booking';
import { ActivitiesView } from '../../components/home';
import { BanepadCentralView, SingleMatchBanepadView } from '../../components/banepad';
import { fetchBookings } from '../../lib/api/bookings';
import { logEntry } from '../../lib/tournamentLog';
import { activityCommandsAreServerAuthoritative, deleteActivities } from '../../lib/api/activities';

export interface BookingActivityBanepadViewsProps {
  view: string;
  // booking branch
  bookings: any;
  bookingInitialDate: any;
  clubInfo: any;
  bookingZones: any;
  bookingConfig: any;
  /** Whether the stored config has opening hours; null before it was read (#1192). */
  bookingHoursSaved?: boolean | null;
  serieBooking: any;
  activeSeries: any;
  updateActiveSeries: any;
  showToast: any;
  setSerieBooking: any;
  setView: (v: View) => void;
  updateBookingZones: any;
  updateBookingConfig: any;
  /** #271: refresh the config after the server changed a court's physical state. */
  applyCourtStateConfig: any;
  /** Equipment beside the courts (docs/domains/ASSET_BOOKING.md). */
  assets?: any[];
  assetBookings?: any[];
  addAssetBooking?: any;
  updateAssetBooking?: any;
  deleteAssetBooking?: any;
  saveAsset?: any;
  deleteAsset?: any;
  addBooking: any;
  addManyBookings: any;
  deleteManyBookings: any;
  replaceSeries: any;
  updateBooking: any;
  deleteBooking: any;
  /** The calendar's «Avbestill og refunder» door (#284), or null without `payment.refund`. */
  staffRefund?: StaffRefundDoor | null;
  registerPlannedMatch: any;
  createLinkedActivity: any;
  plannedMatches: any;
  activities: any;
  setActivitiesOpenId: any;
  setSerieFocusMatchId: any;
  // activities branch
  completeSingleMatchActivity: any;
  recordSingleMatchLiveScore: any;
  effectivePlayerDB: any;
  reopenActivityPicker: any;
  setReopenActivityPicker: any;
  persistNewPlayerImmediate: any;
  persistActivities: any;
  activitiesOpenId: any;
  openActivityReservation: any;
  openActivitySetup: any;
  Storage: any;
  bookingsRef: any;
  Padel24Engine: any;
  validateBooking: any;
  setPendingSetupLoad: any;
  setSetupFromActivities: any;
  setCustomActivityType: any;
  setEditingKMSetup: any;
  estimateSchedule: any;
  makeGroups: any;
  buildFullSchedule: any;
  drawPhase1Matches: any;
  fixPlayerBackToBackConflicts: any;
  ROUNDS_PER_PHASE: any;
  initStandings: any;
  parkCurrentTournament: any;
  nextDisplayTournamentId: any;
  pausedTournaments: any;
  archive: any;
  tournament: any;
  setTournament: any;
  // banepad branch
  recordResultForTournament: any;
  recordKMResultForTournament: any;
  recordKMLiveScore: any;
  recordPadLiveScore: any;
  recordSerieResultForTournament: any;
  recordSerieLiveScore: any;
  banepadTournamentView: any;
  americanoMatchToSlots: any;
  playEndSound: any;
}

/**
 * Booking / activities / banepad view-dispatch group (AppShell step 4). Renders
 * the court-booking calendar, the activities surface and the Banepad central
 * screen for the matching `view`; null otherwise. Branches moved verbatim from
 * the former monolith's `Padel24AppMain` return JSX (extracted from the former
 * monolith, see legacy/App.jsx). The `activities` branch
 * carries the heavy admin handlers (court reserve/release, series delete,
 * activity-start tournament construction) unchanged — they reach the shared
 * engines/Storage/refs through props rather than module imports, matching the
 * other extracted view groups.
 */
export function BookingActivityBanepadViews({
  view,
  bookings,
  bookingInitialDate,
  clubInfo,
  bookingZones,
  bookingConfig,
  bookingHoursSaved = null,
  serieBooking,
  activeSeries,
  updateActiveSeries,
  showToast,
  setSerieBooking,
  setView,
  updateBookingZones,
  updateBookingConfig,
  applyCourtStateConfig,
  assets = [],
  assetBookings = [],
  addAssetBooking = null,
  updateAssetBooking = null,
  deleteAssetBooking = null,
  saveAsset = null,
  deleteAsset = null,
  addBooking,
  addManyBookings,
  deleteManyBookings,
  replaceSeries,
  updateBooking,
  deleteBooking,
  staffRefund = null,
  registerPlannedMatch,
  createLinkedActivity,
  plannedMatches,
  activities,
  setActivitiesOpenId,
  setSerieFocusMatchId,
  completeSingleMatchActivity,
  recordSingleMatchLiveScore,
  effectivePlayerDB,
  reopenActivityPicker,
  setReopenActivityPicker,
  persistNewPlayerImmediate,
  persistActivities,
  activitiesOpenId,
  openActivityReservation,
  openActivitySetup,
  bookingsRef,
  Padel24Engine,
  validateBooking,
  setPendingSetupLoad,
  setSetupFromActivities,
  setCustomActivityType,
  setEditingKMSetup,
  estimateSchedule,
  makeGroups,
  buildFullSchedule,
  drawPhase1Matches,
  fixPlayerBackToBackConflicts,
  ROUNDS_PER_PHASE,
  initStandings,
  parkCurrentTournament,
  nextDisplayTournamentId,
  pausedTournaments,
  archive,
  tournament,
  setTournament,
  recordResultForTournament,
  recordKMResultForTournament,
  recordKMLiveScore,
  recordPadLiveScore,
  recordSerieResultForTournament,
  recordSerieLiveScore,
  banepadTournamentView,
  americanoMatchToSlots,
  playEndSound,
}: BookingActivityBanepadViewsProps) {
  // WHICH COURTS THIS CLUB HAS — the shared answer (`clubEffectiveCourts`),
  // the same set the calendar below derives from the physical count it is
  // given, so the activity handlers allocate and validate against exactly the
  // courts the admin sees. An explicit `enabledCourts` list wins over
  // `courtCount`, which wins over the centre's physical count.
  const effectiveCourts: number[] = clubEffectiveCourts(bookingConfig, clubInfo);
  return (
    <>
      {view === 'bookings' && (
        <BookingView
          bookings={bookings}
          initialDate={bookingInitialDate}
          totalCourts={clubPhysicalCourts(clubInfo)}
          zones={bookingZones}
          config={bookingConfig}
          bookingHoursSaved={bookingHoursSaved}
          assets={assets}
          assetBookings={assetBookings}
          onAddAssetBooking={addAssetBooking}
          onUpdateAssetBooking={updateAssetBooking}
          onDeleteAssetBooking={deleteAssetBooking}
          onSaveAsset={saveAsset}
          onDeleteAsset={deleteAsset}
          serieContext={
            serieBooking
              ? {
                  seriesId: serieBooking.seriesId,
                  matchId: serieBooking.match?.id,
                  classId: serieBooking.classId,
                  title: serieBooking.title,
                  discountPct: serieBooking.discountPct,
                  windowDays: serieBooking.windowDays,
                  onBooked: async (bookingId, data) => {
                    // Link the match to the booking: store bookingId + date/time/court on the match.
                    try {
                      const t = activeSeries;
                      if (t && serieBooking.match?.id) {
                        const info = data
                          ? {
                              date: data.date || null,
                              startTime: data.startTime || null,
                              endTime: data.endTime || null,
                              court: data.court ?? null,
                            }
                          : null;
                        const nextMatches = (t.matches || []).map((m) =>
                          m.id === serieBooking.match.id
                            ? { ...m, bookingId, bookingInfo: info }
                            : m,
                        );
                        await updateActiveSeries({ ...t, matches: nextMatches });
                      }
                    } catch (_e) {
                      /* ignore — the booking is created regardless */
                    }
                    showToast('success', 'Bane reservert og koblet til kampen');
                    setSerieBooking(null);
                    setView('seriespill');
                  },
                }
              : null
          }
          onUpdateZones={updateBookingZones}
          onUpdateConfig={updateBookingConfig}
          onCourtStateChanged={applyCourtStateConfig}
          onBack={() => {
            if (serieBooking) {
              setSerieBooking(null);
              setView('seriespill');
            } else {
              setView('home');
            }
          }}
          onAddBooking={addBooking}
          onAddManyBookings={addManyBookings}
          onDeleteManyBookings={deleteManyBookings}
          onReplaceSeries={replaceSeries}
          onUpdateBooking={updateBooking}
          onDeleteBooking={deleteBooking}
          staffRefund={staffRefund}
          players={effectivePlayerDB}
          onPersistNewPlayer={persistNewPlayerImmediate}
          onRegisterPlannedMatch={registerPlannedMatch}
          onCreateLinkedActivity={createLinkedActivity}
          plannedMatches={plannedMatches}
          onOpenTournament={(seriesId, serieMatchId = null) => {
            // Tournament booking → open the activity/tournament it belongs to.
            if (activities.find((a) => a.id === seriesId)) {
              setActivitiesOpenId(seriesId);
              setView('activities');
            } else if (activeSeries && activeSeries.id === seriesId) {
              // Series-match reservation → open the series play (the match schedule).
              if (serieMatchId) setSerieFocusMatchId(serieMatchId);
              setView('seriespill');
            } else
              showToast('info', 'Fant ikke turneringen — den kan være slettet eller arkivert.');
          }}
        />
      )}
      {view === 'activities' && (
        <ActivitiesView
          effectivePlayerDB={effectivePlayerDB}
          clubInfo={clubInfo}
          seriesForSignup={activeSeries}
          reopenPicker={reopenActivityPicker}
          onPickerReopened={() => setReopenActivityPicker(false)}
          onPersistNewPlayer={persistNewPlayerImmediate}
          onBack={() => {
            setActivitiesOpenId(null);
            setView('home');
          }}
          activities={activities}
          onPersist={persistActivities}
          onCompleteSingleMatchActivity={completeSingleMatchActivity}
          initialSelectedId={activitiesOpenId}
          bookings={bookings}
          onOpenCourtReservation={(activityId) => openActivityReservation(activityId)}
          onEditFullSetup={(activity) => openActivitySetup(activity)}
          onAddCourtToActivity={async (activity) => {
            let all;
            try {
              all = await fetchBookings();
            } catch {
              all = bookingsRef.current || bookings;
            }
            const myB = all.filter((b) => b.seriesId === activity.id);
            let date, startTime, endTime, durationMin;
            if (myB.length) {
              const sample = myB
                .slice()
                .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))[0];
              date = sample.date;
              startTime = sample.startTime;
              endTime = sample.endTime;
              durationMin = sample.durationMin;
            } else {
              const cfg = activity.config || {};
              if (!cfg.activityDate || !cfg.activityTime || !cfg.activityEndTime) {
                showToast('error', 'Sett dato og tid på aktiviteten først.');
                return { error: 'no-time' };
              }
              date = cfg.activityDate;
              startTime = cfg.activityTime;
              endTime = cfg.activityEndTime;
              durationMin =
                Padel24Engine.booking.toMin(endTime) - Padel24Engine.booking.toMin(startTime);
            }
            const sMin = Padel24Engine.booking.toMin(startTime),
              eMin = Padel24Engine.booking.toMin(endTime);
            const occupied = new Set(
              all
                .filter(
                  (b) =>
                    b.date === date &&
                    Padel24Engine.booking.toMin(b.startTime) < eMin &&
                    Padel24Engine.booking.toMin(b.endTime) > sMin,
                )
                .map((b) => Number(b.court)),
            );
            // Allocate from the club's court NUMBERS, never by scanning 1..N
            // (#723): that ignored `enabledCourts`, let `courtCount` beat it,
            // and booked courts the club has closed or does not use. A club on
            // courts [2, 3, 5] is not describable as a count at all.
            const freeCourt = effectiveCourts.find((cc) => !occupied.has(cc)) ?? null;
            if (freeCourt == null) {
              showToast(
                'error',
                `Ingen ledige baner på dette tidspunktet (alle ${effectiveCourts.length} er opptatt).`,
              );
              return { error: 'full' };
            }
            const res = await addManyBookings(
              [
                {
                  court: freeCourt,
                  date,
                  startTime,
                  endTime,
                  durationMin,
                  title: activity.config?.name || 'Aktivitet',
                  seriesId: activity.id,
                  source: 'tournament',
                  paymentStatus: 'free',
                  priceNok: 0,
                },
              ],
              { adminOverride: true },
            );
            if (!res.success || (res.created || 0) < 1) {
              showToast('error', res.notice || res.error || 'Kunne ikke reservere bane.');
              return { error: res.notice || res.error };
            }
            showToast('success', `Bane ${freeCourt} reservert — 4 nye plasser åpnet.`);
            return { created: 1, court: freeCourt };
          }}
          onReserveCourts={async (activity) => {
            const cfg = activity.config || {};
            // Use the ACTUALLY selected time slot/courts from the booking draft — not
            // stale cfg.activityTime or sequential courts (1..n).
            const room0 = (activity.bookingDraft?.slotRooms || [])[0] || {};
            const date = room0.singleDate || cfg.activityDate;
            const startTime = room0.startTime || cfg.activityTime;
            const endTime = room0.endTime || cfg.activityEndTime;
            if (!date || !startTime || !endTime) {
              showToast('error', 'Sett dato, fra og til først.');
              return { error: 'Mangler tid' };
            }
            const startM = Padel24Engine.booking.toMin(startTime);
            const endM = Padel24Engine.booking.toMin(endTime);
            const durationMin = endM - startM;
            if (durationMin <= 0) {
              showToast('error', '«Til» må være etter «Fra».');
              return { error: 'Ugyldig tid' };
            }
            const courtNums = (room0.courtList || [])
              .map((c) => Number(c.num))
              .filter((num) => !isNaN(num));
            // WITHOUT AN EXPLICIT LIST, THE FIRST N OF THE CLUB'S OWN COURTS (#723).
            // This used to be `1..cfg.courts`, which for a club on courts
            // [2, 3, 5] booked court 1 — and `adminOverride` below means neither
            // the browser nor the server checks the court against the club's
            // set, so this line alone decided which courts were booked. An
            // explicit `courtList` from the draft is the operator's own choice
            // and is kept as given.
            const requested = Math.max(1, Number(cfg.courts) || 1);
            if (!courtNums.length && requested > effectiveCourts.length) {
              showToast(
                'error',
                `Aktiviteten trenger ${requested} baner, men klubben har bare ${effectiveCourts.length}. Velg baner i bookingutkastet eller reduser antallet.`,
              );
              return { error: 'not_enough_courts' };
            }
            const nums = courtNums.length ? courtNums : effectiveCourts.slice(0, requested);
            const n = nums.length;
            // Smartbooking: warn if the selected courts break the rules (dead gap, etc.).
            // The admin reserves anyway, but should be notified — as before.
            const smartWarn = [];
            if (bookingConfig.smartAllocation !== false) {
              const bufferMin = bookingConfig.bufferMin ?? Padel24Engine.booking.DEFAULT_BUFFER_MIN;
              const minBM =
                bookingConfig.allowedDurations && bookingConfig.allowedDurations.length
                  ? Math.min(...bookingConfig.allowedDurations)
                  : 60;
              // `effectiveCourts` is always an array, so the count is its
              // length — the old `enabled ? … : clubInfo.courts || 4` fallback
              // was unreachable and gave an unconfigured club 0 courts (#723).
              const sOpts = {
                bufferMin,
                minBookingMin: minBM,
                smartAllocation: true,
                totalCourts: effectiveCourts.length,
                enabledCourts: effectiveCourts,
                excludeCourts: nums,
              };
              const running = (bookingsRef.current || bookings)
                .filter((b) => b.date === date && b.seriesId !== activity.id)
                .map((b) => ({ ...b }));
              for (const num of nums) {
                const v = validateBooking({ court: num, date, startTime, endTime }, running, sOpts);
                if (!v.ok && !/allerede booket/.test(v.reason || '')) smartWarn.push(`Bane ${num}`);
                running.push({ court: num, date, startTime, endTime, id: `plan_${num}` });
              }
            }
            const existingIds = (bookingsRef.current || bookings)
              .filter((b) => b.seriesId === activity.id)
              .map((b) => b.id);
            const list = nums.map((num) => ({
              court: num,
              date,
              startTime,
              endTime,
              durationMin,
              title: cfg.name || 'Aktivitet',
              seriesId: activity.id,
              source: 'tournament',
              paymentStatus: 'free',
              priceNok: 0,
            }));
            const res = await addManyBookings(list, {
              adminOverride: true,
              deleteIds: existingIds,
            });
            if (!res.success) {
              showToast('error', res.error || 'Kunne ikke reservere banetid.');
              return { error: res.error };
            }
            if (res.notice) showToast('info', res.notice, 9000);
            else if (res.created < n)
              showToast(
                'info',
                `${res.created} av ${n} baner reservert (${n - res.created} opptatt).`,
              );
            else if (smartWarn.length)
              showToast(
                'info',
                `Reservert, men ${smartWarn.join(', ')} bryter smartbooking (kan lage dødt hull mot nabo-booking). Vurder et annet tidsrom.`,
                9000,
              );
            else
              showToast(
                'success',
                `${res.created} bane${res.created === 1 ? '' : 'r'} reservert i kalenderen.`,
              );
            return { created: res.created, requested: n };
          }}
          onReleaseCourts={async (activity) => {
            const ids = (bookingsRef.current || bookings)
              .filter((b) => b.seriesId === activity.id)
              .map((b) => b.id);
            if (!ids.length) return { removed: 0 };
            const res = await deleteManyBookings(ids);
            showToast(
              'success',
              `Frigjorde ${res.removed} banereservasjon${res.removed === 1 ? '' : 'er'}.`,
            );
            return { removed: res.removed };
          }}
          onReleaseFutureCourts={async (activity) => {
            // Remove only bookings dated today or later — historical ones are kept.
            const _td = new Date();
            const todayISO = `${_td.getFullYear()}-${String(_td.getMonth() + 1).padStart(2, '0')}-${String(_td.getDate()).padStart(2, '0')}`;
            const ids = (bookingsRef.current || bookings)
              .filter((b) => b.seriesId === activity.id && b.date >= todayISO)
              .map((b) => b.id);
            if (!ids.length) return { removed: 0 };
            const res = await deleteManyBookings(ids);
            showToast(
              'success',
              `Fjernet ${res.removed} fremtidig${res.removed === 1 ? '' : 'e'} banereservasjon${res.removed === 1 ? '' : 'er'}.`,
            );
            return { removed: res.removed };
          }}
          onReleaseCourtNumbers={async (activity, courtNums) => {
            // Release only bookings for the given court numbers (the redundant ones).
            // Read fresh from Storage and compare court as a NUMBER (the panel may store court as a string).
            let all;
            try {
              all = await fetchBookings();
            } catch {
              all = bookingsRef.current || bookings;
            }
            const set = new Set(courtNums.map((c) => Number(c)));
            const ids = all
              .filter((b) => b.seriesId === activity.id && set.has(Number(b.court)))
              .map((b) => b.id);
            if (!ids.length) {
              showToast('info', 'Fant ingen banereservasjon å frigjøre for denne aktiviteten.');
              return { removed: 0 };
            }
            const res = await deleteManyBookings(ids);
            showToast(
              'success',
              `Frigjorde ${res.removed} banereservasjon${res.removed === 1 ? '' : 'er'} (bane ${courtNums.join(', ')}).`,
            );
            return { removed: res.removed };
          }}
          onDeleteActivitySeries={async (activity, scope = 'all') => {
            const recId = activity.recurringId;
            if (!recId) return { deleted: 0, removed: 0 };
            const dateOf = (a) => (a.bookingDraft?.slotRooms || [])[0]?.singleDate || '';
            const myDate = dateOf(activity);
            const inScope = (a) => {
              if (a.recurringId !== recId) return false;
              if (scope === 'all') return true;
              if (scope === 'future') return dateOf(a) >= myDate;
              return a.id === activity.id; // 'this'
            };
            const occs = (activities || []).filter(inScope);
            const occIds = new Set(occs.map((a) => a.id));
            const bkIds = (bookingsRef.current || bookings)
              .filter((b) => occIds.has(b.seriesId))
              .map((b) => b.id);
            if (activityCommandsAreServerAuthoritative()) {
              // ONE COMMAND, ONE TRANSACTION. The client used to delete the
              // bookings and then the activities in two calls: a failure
              // between them released the courts and left the activities
              // standing, with no error. The server derives which courts to
              // free from the occurrences' own `seriesId`, and derives the
              // OCCURRENCES from the series and the anchor — so «future» is
              // what the data says, not what the client sent.
              await deleteActivities({
                recurring: { recurringId: recId, scope, anchorId: activity.id },
                releaseBookings: true,
              });
            } else {
              if (bkIds.length) await deleteManyBookings(bkIds);
              await persistActivities((activities || []).filter((a) => !occIds.has(a.id)));
            }
            showToast(
              'success',
              `Slettet ${occs.length} aktivitet${occs.length === 1 ? '' : 'er'} og ${bkIds.length} banereservasjon${bkIds.length === 1 ? '' : 'er'}.`,
            );
            return { deleted: occs.length, removed: bkIds.length };
          }}
          onDeleteAllActivities={async () => {
            // DEMO: delete all activities + release all court reservations tied to them.
            const actIds = new Set((activities || []).map((a) => a.id));
            const bkIds = (bookingsRef.current || bookings)
              .filter((b) => actIds.has(b.seriesId))
              .map((b) => b.id);
            // IT NAMES WHAT IT DELETES (#245). This is a demo/reset button, not
            // a product flow, and «delete every activity for this club» is a
            // worse thing as an API verb than as a button — an empty or stale
            // request would mean «everything», which is precisely the shape the
            // whole-collection PUT had. So the button enumerates the ids it can
            // see and sends them to the ordinary delete, cascading the courts
            // the same command already frees.
            if (activityCommandsAreServerAuthoritative()) {
              if (actIds.size) {
                await deleteActivities({ ids: [...actIds].map(String), releaseBookings: true });
              }
            } else {
              if (bkIds.length) await deleteManyBookings(bkIds);
              await persistActivities([]);
            }
            showToast(
              'success',
              `Slettet ${actIds.size} aktivitet${actIds.size === 1 ? '' : 'er'} og frigjorde ${bkIds.length} banereservasjon${bkIds.length === 1 ? '' : 'er'}.`,
            );
          }}
          onCreateViaActivities={(typeId) => {
            setPendingSetupLoad?.({ viaActivities: true });
            setSetupFromActivities(true);
            // Custom activities (coaching/event/custom) reuse the americano
            // registration base, but are tagged with their own format.
            const isCustom = ['coaching', 'arrangement', 'custom'].includes(typeId);
            setCustomActivityType(isCustom ? typeId : null);
            const viewByType = {
              coaching: 'americanoSetup',
              arrangement: 'americanoSetup',
              custom: 'americanoSetup',
              padel24: 'wizard',
              klubbmesterskap: 'klubbmesterskapSetup',
              single_matches: 'singleMatches',
              americano: 'americanoSetup',
              team_americano: 'teamAmericanoSetup',
              mexicano: 'mexicanoSetup',
              mixicano: 'mixicanoSetup',
              team_mexicano: 'teamMexicanoSetup',
              mix_americano: 'mixAmericanoSetup',
              club_americano: 'clubAmericanoSetup',
              club_team_americano: 'clubTeamAmericanoSetup',
              club_mexicano: 'clubMexicanoSetup',
            };
            const target = viewByType[typeId];
            if (!target) {
              showToast('info', 'Dette formatet kan ikke opprettes herfra ennå.');
              return;
            }
            setActivitiesOpenId(null);
            setView(target);
          }}
          onStartActivity={async (activity) => {
            const fmt = activity.config?.tournamentType || 'americano';
            const confirmedRegs = (activity.registrations || []).filter(
              (r) => r.status === 'confirmed',
            );
            // KLUBBMESTERSKAP: open the KM wizard pre-filled with the registered pairs per class.
            if (fmt === 'klubbmesterskap') {
              const snapshot = activity.config?.kmSnapshot
                ? JSON.parse(JSON.stringify(activity.config.kmSnapshot))
                : null;
              if (!snapshot) {
                showToast('error', 'Mangler KM-oppsett på aktiviteten.');
                return;
              }
              const byBucket = {};
              confirmedRegs.forEach((r) => {
                if (!r.bucketKey || !r.partnerId) return;
                (byBucket[r.bucketKey] = byBucket[r.bucketKey] || []).push({
                  id: `pair_${r.playerId}_${r.partnerId}`,
                  p1Id: r.playerId,
                  p2Id: r.partnerId,
                });
              });
              const activeBuckets = Object.keys(byBucket).filter((k) => byBucket[k].length >= 2);
              if (activeBuckets.length === 0) {
                showToast(
                  'error',
                  'Minst 2 par i minst én klasse kreves for å starte klubbmesterskapet.',
                );
                return;
              }
              snapshot.registeredPairs = byBucket;
              snapshot.kmSignupMode = 'activity';
              snapshot.kmEditActivity = true;
              snapshot.kmAutoPreview = true;
              setEditingKMSetup({
                snapshot,
                tournamentId: null,
                fromActivityId: activity.id,
                editActivity: true,
              });
              setView('klubbmesterskapSetup');
              showToast('success', 'Åpner forhåndsvisning med de påmeldte parene.');
              return;
            }
            const isTeamFmt = /team/.test(fmt);
            let players,
              cfg = activity.config;
            const clubsCfg = Array.isArray(activity.config?.clubs) ? activity.config.clubs : [];
            const isClubTeamFmt = fmt === 'club_team_americano' && clubsCfg.length === 2;
            if (isClubTeamFmt) {
              // CLUB TEAM: build teams PER CLUB (pairs + pairing of singles within the same club) + membership.
              const strat = activity.config?.singlePairingStrategy || 'balanced';
              const pairSingles = (ids) => {
                const withElo = ids
                  .map((id) => ({
                    id,
                    elo: effectivePlayerDB.find((p) => p.id === id)?.elo ?? 1000,
                  }))
                  .sort((a, b) => b.elo - a.elo);
                const out = [];
                if (strat === 'similar') {
                  for (let i = 0; i + 1 < withElo.length; i += 2)
                    out.push({ p1Id: withElo[i].id, p2Id: withElo[i + 1].id });
                } else {
                  let lo = 0,
                    hi = withElo.length - 1;
                  while (lo < hi) {
                    out.push({ p1Id: withElo[lo].id, p2Id: withElo[hi].id });
                    lo++;
                    hi--;
                  }
                }
                return out;
              };
              const teams = [];
              const membership = {};
              for (const club of clubsCfg) {
                const cRegs = confirmedRegs.filter((r) => r.clubId === club.id);
                const cPairs = cRegs
                  .filter((r) => r.partnerId)
                  .map((r) => ({ p1Id: r.playerId, p2Id: r.partnerId }));
                const cSingleTeams = pairSingles(
                  cRegs.filter((r) => !r.partnerId).map((r) => r.playerId),
                );
                for (const t of [...cPairs, ...cSingleTeams]) {
                  teams.push(t);
                  membership[t.p1Id] = club.id;
                  membership[t.p2Id] = club.id;
                }
              }
              const ids = teams.flatMap((t) => [t.p1Id, t.p2Id]);
              players = ids.map((id) => effectivePlayerDB.find((p) => p.id === id)).filter(Boolean);
              cfg = { ...activity.config, teamMode: true, teams, clubMembership: membership };
              if (players.length < 4) {
                showToast('error', 'Minst 2 lag (4 spillere) kreves for å starte.');
                return;
              }
            } else if (isTeamFmt) {
              // Team format: build fixed teams from (a) ready-made pairs and (b) singles
              // that are paired at start by the chosen ELO strategy. config.teams keeps them fixed.
              const pairRegs = confirmedRegs.filter((r) => r.partnerId);
              const singleIds = confirmedRegs.filter((r) => !r.partnerId).map((r) => r.playerId);
              const strat = activity.config?.singlePairingStrategy || 'balanced';
              const withElo = singleIds
                .map((id) => ({ id, elo: effectivePlayerDB.find((p) => p.id === id)?.elo ?? 1000 }))
                .sort((a, b) => b.elo - a.elo);
              const singleTeams = [];
              if (strat === 'similar') {
                // Equally strong together: neighbours in the ELO-sorted list. Odd number → the last single sits out.
                for (let i = 0; i + 1 < withElo.length; i += 2)
                  singleTeams.push({ p1Id: withElo[i].id, p2Id: withElo[i + 1].id });
              } else {
                // Balanced teams: strongest + weakest. Odd number → the middle single sits out.
                let lo = 0,
                  hi = withElo.length - 1;
                while (lo < hi) {
                  singleTeams.push({ p1Id: withElo[lo].id, p2Id: withElo[hi].id });
                  lo++;
                  hi--;
                }
              }
              const teams = [
                ...pairRegs.map((r) => ({ p1Id: r.playerId, p2Id: r.partnerId })),
                ...singleTeams,
              ];
              const ids = teams.flatMap((t) => [t.p1Id, t.p2Id]);
              players = ids.map((id) => effectivePlayerDB.find((p) => p.id === id)).filter(Boolean);
              cfg = { ...activity.config, teamMode: true, teams };
              if (players.length < 4) {
                showToast('error', 'Minst 2 lag (4 spillere) kreves for å starte.');
                return;
              }
            } else {
              players = confirmedRegs
                .map((r) => r.playerId)
                .map((id) => effectivePlayerDB.find((p) => p.id === id))
                .filter(Boolean);
              if (players.length < 4) {
                showToast('error', 'Minst 4 spillere kreves for å starte.');
                return;
              }
              // CLUB: build clubMembership from the registrations' club choice + require equal numbers from each club.
              const clubsCfg = Array.isArray(activity.config?.clubs) ? activity.config.clubs : [];
              if (clubsCfg.length === 2) {
                const membership = {};
                confirmedRegs.forEach((r) => {
                  if (r.clubId) membership[r.playerId] = r.clubId;
                });
                const aCount = Object.values(membership).filter((x) => x === clubsCfg[0].id).length;
                const bCount = Object.values(membership).filter((x) => x === clubsCfg[1].id).length;
                if (aCount < 2 || bCount < 2 || aCount !== bCount) {
                  showToast(
                    'error',
                    `Trenger like mange fra hver klubb (nå ${aCount} ${clubsCfg[0].name} vs ${bCount} ${clubsCfg[1].name}, minst 2 hver).`,
                  );
                  return;
                }
                cfg = { ...activity.config, clubMembership: membership };
              }
            }
            let created;
            if (fmt === 'padel24') {
              // PADEL24 is a slot format: engine.create alone does not produce groups/matches.
              // Build the full match plan the way the wizard's handleStart does (groups → schedule → phase 1 draw).
              const p24cfg = { ...(activity.config?.p24Snapshot || activity.config || {}) };
              p24cfg.name = activity.config?.name || p24cfg.name || 'Padel24';
              if (players.length < 4) {
                showToast('error', 'Minst 4 spillere kreves for å starte.');
                return;
              }
              const schedule = estimateSchedule(p24cfg, players.length);
              // Restore any saved group order from the previous preview
              // (the admin may have edited the groups). New registrants are appended at the end.
              let groups;
              const savedOrder = activity.config?.p24PreviewGroupOrder;
              if (Array.isArray(savedOrder) && savedOrder.length) {
                const byId = new Map(players.map((p) => [p.id, p]));
                const ordered = [];
                savedOrder.forEach((id) => {
                  if (byId.has(id)) {
                    ordered.push(byId.get(id));
                    byId.delete(id);
                  }
                });
                [...byId.values()]
                  .sort((a: any, b: any) => (b.elo || 0) - (a.elo || 0))
                  .forEach((p) => ordered.push(p));
                const numGroups = Math.max(
                  2,
                  Math.round(ordered.length / (p24cfg.playersPerGroup || 12)),
                );
                const per = Math.ceil(ordered.length / numGroups);
                groups = Array.from({ length: numGroups }, (_, i) => ({
                  letter: String.fromCharCode(65 + i),
                  players: [],
                }));
                ordered.forEach((p, i) => {
                  groups[Math.min(numGroups - 1, Math.floor(i / per))].players.push(p);
                });
              } else {
                groups = makeGroups(players, p24cfg.playersPerGroup);
              }
              let allMatches;
              let restoredEdited = false;
              // Restore the SAVED draw if the player set is unchanged
              // since the last preview — so that phase 1 is not random every
              // time. If the players have changed (added/withdrawn), it is drawn again.
              const savedMatches = activity.config?.p24PreviewMatches;
              const savedPlayerIds = activity.config?.p24PreviewPlayerIds;
              const curIds = players.map((p) => p.id);
              const samePlayers =
                Array.isArray(savedPlayerIds) &&
                savedPlayerIds.length === curIds.length &&
                savedPlayerIds.every((id) => curIds.includes(id));
              if (samePlayers && Array.isArray(savedMatches) && savedMatches.length) {
                allMatches = savedMatches;
                restoredEdited = !!activity.config?.p24PreviewGroupsEdited;
              } else {
                allMatches = buildFullSchedule(p24cfg, groups, schedule);
                if (allMatches.some((m) => m.phase === 1 && m.needsDraw)) {
                  allMatches = drawPhase1Matches(
                    allMatches,
                    groups,
                    { mode: p24cfg.drawingMode || 'varied', ...(p24cfg.drawRules || {}) },
                    players,
                  );
                  fixPlayerBackToBackConflicts(allMatches, p24cfg, 1);
                }
              }
              if (!groups || groups.length === 0 || !allMatches || allMatches.length === 0) {
                showToast(
                  'error',
                  'Kunne ikke bygge kampplan for Padel24 (sjekk oppsett/antall spillere).',
                );
                return;
              }
              created = {
                id: `pdl_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
                status: 'ready',
                previewActivityId: activity.id,
                config: p24cfg,
                club: clubInfo,
                players: players.map((p) => ({ ...p, startElo: p.elo })),
                groups,
                phase: 1,
                phasesTotal: schedule.phases,
                round: 1,
                roundsPerPhase: ROUNDS_PER_PHASE,
                matches: allMatches,
                groupsEditedSinceDraw: restoredEdited,
                standings: initStandings(players),
                groupRankings: {},
                groupHistory: {},
                finalRanking: null,
                // A code, not a Norwegian sentence (#519).
                log: [logEntry('previewBuilt', { matches: allMatches.length })],
              };
            } else {
              const engine = Padel24Engine[fmt] || Padel24Engine.americano;
              created = engine.create({ config: cfg, players });
              if (created.error) {
                showToast('error', created.error);
                return;
              }
            }
            // Standby reserves (padel24): carry into the tournament, sorted by order.
            // NO TELEPHONE (#1079): the screens that offer to ring a reserve look
            // the number up in the club's list by `playerId`, so a copy here was
            // only another place a person's number was kept.
            const standbyReserves = (activity.registrations || [])
              .filter((r) => r.standbyReserve && r.playerId)
              .sort((a, b) => (a.standbyAt || 0) - (b.standbyAt || 0))
              .map((r, i) => {
                const p = effectivePlayerDB.find((x) => x.id === r.playerId);
                return {
                  playerId: r.playerId,
                  name: p?.name || 'Ukjent',
                  elo: p?.elo ?? null,
                  level: p?.level ?? null,
                  gender: p?.gender ?? null,
                  standbyDays: r.standbyDays || null,
                  standbyAt: r.standbyAt || null,
                  order: i + 1,
                  standbyOnly: !!r.standbyOnly,
                  used: false,
                };
              });
            // Padel24 starts as a PREVIEW — keep the activity so registration/reserve stays open.
            // Other formats commit directly (the activity is removed).
            if (fmt !== 'padel24')
              if (activityCommandsAreServerAuthoritative()) {
                // No cascade: the activity is consumed into a tournament and its
                // courts stay, because they now belong to that tournament.
                await deleteActivities({ ids: [activity.id], releaseBookings: false });
              } else {
                await persistActivities(activities.filter((a) => a.id !== activity.id));
              }
            parkCurrentTournament();
            const displayId = nextDisplayTournamentId(
              [...pausedTournaments, ...archive.map((a) => a.data || {}), tournament].filter(
                Boolean,
              ),
            );
            setTournament({ ...created, displayId, standbyReserves });
            const viewByType = {
              padel24: 'tournament',
              americano: 'americano',
              team_americano: 'americano',
              mexicano: 'mexicano',
              mixicano: 'mixicano',
              team_mexicano: 'teamMexicano',
              mix_americano: 'mixAmericano',
              club_americano: 'clubAmericano',
              club_team_americano: 'clubTeamAmericano',
              club_mexicano: 'clubMexicano',
            };
            setView(viewByType[fmt] || 'americano');
          }}
        />
      )}
      {/* BANEPAD: central main screen + pads at each court */}
      {view === 'banepad' && (
        <BanepadCentralView
          bookings={bookings}
          bookingConfig={bookingConfig}
          clubInfo={clubInfo}
          tournament={tournament}
          pausedTournaments={pausedTournaments}
          activeSeries={activeSeries}
          activities={activities}
          plannedMatches={plannedMatches}
          allPlayers={effectivePlayerDB}
          onRecordResult={recordResultForTournament}
          onRecordKMResult={recordKMResultForTournament}
          onRecordKMLiveScore={recordKMLiveScore}
          onRecordPadLiveScore={recordPadLiveScore}
          onRecordSerieResult={recordSerieResultForTournament}
          onRecordSerieLiveScore={recordSerieLiveScore}
          onRecordSingleMatchResult={completeSingleMatchActivity}
          onLiveSingleMatchScore={recordSingleMatchLiveScore}
          onOpenTournamentView={(type, t) => {
            if (t && t.id && tournament?.id !== t.id) setTournament(t);
            setView(banepadTournamentView(type));
          }}
          onOpenSeriesView={() => setView('seriespill')}
          onBack={() => setView('home')}
          americanoMatchToSlots={americanoMatchToSlots}
          Padel24Engine={Padel24Engine}
          playEndSound={playEndSound}
        />
      )}
      {/* BANEPAD: enkeltkamp pads (per court), opened from the Enkeltkamper page */}
      {view === 'singleMatchesBanepad' && (
        <SingleMatchBanepadView
          activities={activities}
          plannedMatches={plannedMatches}
          bookings={bookings}
          bookingConfig={bookingConfig}
          allPlayers={effectivePlayerDB}
          onRecordSingleMatchResult={completeSingleMatchActivity}
          onLiveSingleMatchScore={recordSingleMatchLiveScore}
          onBack={() => setView('singleMatches')}
          americanoMatchToSlots={americanoMatchToSlots}
          Padel24Engine={Padel24Engine}
          playEndSound={playEndSound}
        />
      )}
    </>
  );
}
